import React, { useMemo, useState } from "react";
import { FiShuffle, FiExternalLink, FiCheck, FiX } from "react-icons/fi";
import { slugify } from "../../utils/slug";
import { toDayKey } from "../../utils/stats";

const GOAL_KEY = "dailyGoal";
const PLAN_KEY = "dailyPlan";
const MIN_GOAL = 1;
const MAX_GOAL = 15;

function readGoal() {
  const raw = Number(window.localStorage[GOAL_KEY]);
  if (!raw || isNaN(raw)) return 3;
  return Math.min(MAX_GOAL, Math.max(MIN_GOAL, Math.round(raw)));
}

function readPlan(today) {
  try {
    const p = JSON.parse(window.localStorage[PLAN_KEY] || "null");
    if (p && p.date === today && Array.isArray(p.items)) return p;
  } catch (e) {
    /* ignore malformed plan */
  }
  return null;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// A monochrome progress ring rendered with a conic-gradient.
function Ring({ solved, goal }) {
  const pct = goal > 0 ? Math.min(100, Math.round((solved / goal) * 100)) : 0;
  return (
    <div
      className="dg-ring"
      style={{ background: `conic-gradient(var(--nb-text) ${pct * 3.6}deg, var(--nb-bg-sunken) 0)` }}
      role="img"
      aria-label={`${solved} of ${goal} solved today`}
    >
      <div className="dg-ring-hole">
        <span className="dg-ring-num">{solved}</span>
        <span className="dg-ring-den">/ {goal}</span>
      </div>
    </div>
  );
}

export default function DailyGoal({ questionData, updateData }) {
  const today = toDayKey(new Date());
  const [goal, setGoal] = useState(readGoal);
  const [plan, setPlan] = useState(() => readPlan(today));

  // Problems solved today (from anywhere in the app).
  const solvedToday = useMemo(() => {
    let n = 0;
    (questionData || []).forEach((t) =>
      (t.questions || []).forEach((q) => {
        if (q.Done && toDayKey(q.SolvedAt) === today) n += 1;
      })
    );
    return n;
  }, [questionData, today]);

  // Every unsolved problem, flattened, for random selection.
  const unsolved = useMemo(() => {
    const out = [];
    (questionData || []).forEach((t) =>
      (t.questions || []).forEach((q) => {
        if (!q.Done) out.push({ slug: slugify(t.topicName), topic: t.topicName, problem: q.Problem, url: q.URL || "" });
      })
    );
    return out;
  }, [questionData]);

  function saveGoal(v) {
    const g = Math.min(MAX_GOAL, Math.max(MIN_GOAL, Number(v)));
    setGoal(g);
    window.localStorage[GOAL_KEY] = g;
  }

  function generate() {
    const items = shuffle(unsolved).slice(0, goal);
    const next = { date: today, items };
    setPlan(next);
    window.localStorage[PLAN_KEY] = JSON.stringify(next);
  }

  function clearPlan() {
    setPlan(null);
    window.localStorage.removeItem(PLAN_KEY);
  }

  // Live "is this plan item solved?" by resolving against current data.
  function isItemSolved(item) {
    const topic = (questionData || []).find((t) => slugify(t.topicName) === item.slug);
    if (!topic) return false;
    const q = (topic.questions || []).find((x) => x.Problem === item.problem);
    return !!(q && q.Done);
  }

  function markSolved(item) {
    const ti = (questionData || []).findIndex((t) => slugify(t.topicName) === item.slug);
    if (ti === -1) return;
    const topic = questionData[ti];
    const pi = (topic.questions || []).findIndex((x) => x.Problem === item.problem);
    if (pi === -1) return;
    const questions = topic.questions.map((q, i) =>
      i === pi ? { ...q, Done: true, SolvedAt: q.SolvedAt || new Date().toISOString() } : q
    );
    const done = questions.filter((q) => q.Done).length;
    updateData(slugify(topic.topicName), { started: done > 0, doneQuestions: done, questions }, ti);
  }

  const reached = solvedToday >= goal;

  return (
    <div className="dash-section dg-section">
      <h5>Today’s goal</h5>
      <div className="dg-top">
        <Ring solved={solvedToday} goal={goal} />
        <div className="dg-controls">
          <div className="dg-goal-row">
            <span className="dg-goal-lbl">Daily target</span>
            <span className="dg-goal-val">{goal} problem{goal === 1 ? "" : "s"}</span>
          </div>
          <input
            type="range"
            className="dg-slider"
            min={MIN_GOAL}
            max={MAX_GOAL}
            value={goal}
            onChange={(e) => saveGoal(e.target.value)}
            aria-label="Daily problem goal"
          />
          <div className="dg-status">
            {reached ? (
              <span className="dg-done"><FiCheck size={14} /> Goal reached for today — nice.</span>
            ) : (
              <span>{goal - solvedToday} to go. Solve your own, or let us pick a set.</span>
            )}
          </div>
          <div className="dg-actions">
            <button type="button" className="nb-btn nb-btn-sm" onClick={generate} disabled={unsolved.length === 0}>
              <FiShuffle size={14} /> {plan ? "Shuffle set" : `Pick ${goal} random for me`}
            </button>
            {plan ? (
              <button type="button" className="nb-btn nb-btn-sm" onClick={clearPlan}>
                <FiX size={14} /> Clear set
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {plan ? (
        plan.items.length === 0 ? (
          <div className="dg-empty">Nothing left to pick — every problem is solved. 🎉</div>
        ) : (
          <ul className="dg-plan">
            {plan.items.map((item, i) => {
              const solved = isItemSolved(item);
              return (
                <li key={`${item.slug}-${i}`} className={`dg-item ${solved ? "solved" : ""}`}>
                  <span className="dg-item-check" aria-hidden="true">{solved ? <FiCheck size={14} /> : i + 1}</span>
                  <span className="dg-item-name" title={item.problem}>{item.problem}</span>
                  <span className="dg-item-topic">{item.topic}</span>
                  {item.url ? (
                    <a className="nb-btn nb-btn-sm" href={item.url} target="_blank" rel="noopener noreferrer">
                      <FiExternalLink size={13} /> Open
                    </a>
                  ) : null}
                  <button type="button" className="nb-btn nb-btn-sm" onClick={() => markSolved(item)} disabled={solved}>
                    {solved ? "Solved" : "Mark solved"}
                  </button>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </div>
  );
}
